import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import {
  Shield, Eye, Radio, CreditCard, ChevronRight,
  AlertTriangle, CheckCircle, MapPin, User, Phone, Mail,
  Zap, Package, RefreshCw
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TOOL_ICONS = {
  smartship: MapPin,
  supertrak: Radio,
  preauth_checker: CreditCard,
  supercheck_plus: Shield
};

const TOOL_COLORS = {
  smartship: { bg: 'bg-blue-50 dark:bg-blue-900/20', border: 'border-blue-200 dark:border-blue-800', icon: 'text-blue-600', badge: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' },
  supertrak: { bg: 'bg-purple-50 dark:bg-purple-900/20', border: 'border-purple-200 dark:border-purple-800', icon: 'text-purple-600', badge: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300' },
  preauth_checker: { bg: 'bg-amber-50 dark:bg-amber-900/20', border: 'border-amber-200 dark:border-amber-800', icon: 'text-amber-600', badge: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300' },
  supercheck_plus: { bg: 'bg-emerald-50 dark:bg-emerald-900/20', border: 'border-emerald-200 dark:border-emerald-800', icon: 'text-emerald-600', badge: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' }
};

const PremiumToolsPage = () => {
  const { token } = useAuth();
  const [tools, setTools] = useState(null);
  const [activeTool, setActiveTool] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  // SmartShip form
  const [smartshipForm, setSmartshipForm] = useState({
    address_street: '', address_city: '', address_state: '', address_zip: '',
    recipient_name: '', package_value: 100, delivery_method: 'standard'
  });

  // SuperTrak form
  const [supertrakForm, setSupertrakForm] = useState({
    shipment_id: '', package_value: 100, special_instructions: '',
    contact_phone: '', contact_email: ''
  });

  // PreAuth form
  const [preauthForm, setPreauthForm] = useState({
    transaction_id: '', amount: 100, card_last4: '', billing_zip: '',
    shipping_zip: '', email: '', ip_address: ''
  });

  // SuperCheck Plus form
  const [supercheckForm, setSupercheckForm] = useState({
    shipment_id: '', package_value: 500, item_description: '',
    sender_verified: false, recipient_verified: false
  });

  useEffect(() => {
    fetchTools();
  }, []);

  const fetchTools = async () => {
    try {
      const res = await fetch(`${API_URL}/api/premium-tools/available`);
      if (res.ok) setTools(await res.json());
    } catch (err) {
      console.error('Tools fetch error:', err);
    }
  };

  const runSmartShip = async () => {
    setLoading(true); setResult(null);
    try {
      const res = await fetch(`${API_URL}/api/premium-tools/smartship/analyze`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(smartshipForm)
      });
      if (res.ok) setResult({ tool: 'smartship', data: await res.json() });
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const runSuperTrak = async () => {
    setLoading(true); setResult(null);
    try {
      const res = await fetch(`${API_URL}/api/premium-tools/supertrak/activate`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(supertrakForm)
      });
      if (res.ok) setResult({ tool: 'supertrak', data: await res.json() });
      else {
        const err = await res.json();
        setResult({ tool: 'supertrak', error: err.detail || 'Failed' });
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const runPreAuth = async () => {
    setLoading(true); setResult(null);
    try {
      const res = await fetch(`${API_URL}/api/premium-tools/preauth-checker/analyze`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(preauthForm)
      });
      if (res.ok) setResult({ tool: 'preauth_checker', data: await res.json() });
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const runSuperCheck = async () => {
    setLoading(true); setResult(null);
    try {
      const res = await fetch(`${API_URL}/api/premium-tools/supercheck-plus/activate`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(supercheckForm)
      });
      if (res.ok) setResult({ tool: 'supercheck_plus', data: await res.json() });
      else {
        const err = await res.json();
        setResult({ tool: 'supercheck_plus', error: err.detail || 'Failed' });
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const riskColor = (level) => {
    if (level === 'high') return 'text-red-600 bg-red-50 dark:bg-red-900/20';
    if (level === 'medium') return 'text-amber-600 bg-amber-50 dark:bg-amber-900/20';
    return 'text-emerald-600 bg-emerald-50 dark:bg-emerald-900/20';
  };

  return (
    <div className="space-y-6" data-testid="premium-tools-page">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">SelleSend Premium Tools</h1>
        <p className="text-slate-500 mt-1">Advanced protection, monitoring, and fraud detection</p>
      </div>

      {/* Tool Cards Grid */}
      {tools && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {Object.entries(tools.tools).map(([key, tool]) => {
            const Icon = TOOL_ICONS[key];
            const colors = TOOL_COLORS[key];
            const isActive = activeTool === key;
            return (
              <button
                key={key}
                onClick={() => { setActiveTool(isActive ? null : key); setResult(null); }}
                className={`text-left p-5 rounded-xl border transition-all ${
                  isActive ? `${colors.bg} ${colors.border} shadow-md` : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 hover:shadow-sm'
                }`}
                data-testid={`tool-card-${key}`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className={`w-10 h-10 rounded-lg ${colors.bg} flex items-center justify-center`}>
                    <Icon className={`w-5 h-5 ${colors.icon}`} />
                  </div>
                  <span className={`text-xs font-semibold px-2 py-1 rounded-full ${colors.badge}`}>
                    ${tool.price.toFixed(2)}
                  </span>
                </div>
                <h3 className="font-semibold text-sm">{tool.name}</h3>
                <p className="text-xs text-slate-500 mt-1 line-clamp-2">{tool.description}</p>
              </button>
            );
          })}
        </div>
      )}

      {/* Bundles */}
      {tools && (
        <div className="flex flex-wrap gap-3">
          {Object.entries(tools.bundle_discount).map(([key, bundle]) => (
            <div key={key} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-100 dark:bg-slate-800 text-sm">
              <Zap className="w-3.5 h-3.5 text-amber-500" />
              <span className="font-medium">{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
              <span className="text-slate-400 line-through">${bundle.regular_price}</span>
              <span className="font-semibold text-emerald-600">${bundle.bundle_price}</span>
              <span className="text-xs text-emerald-500">Save ${bundle.savings}</span>
            </div>
          ))}
        </div>
      )}

      {/* Active Tool Form */}
      {activeTool && (
        <div className="grid lg:grid-cols-2 gap-6">
          <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-6">
            {activeTool === 'smartship' && (
              <>
                <h3 className="font-semibold mb-4 flex items-center gap-2"><MapPin className="w-4 h-4 text-blue-600" /> SmartShip Analysis</h3>
                <div className="space-y-3">
                  <input value={smartshipForm.recipient_name} onChange={e => setSmartshipForm(p => ({...p, recipient_name: e.target.value}))} placeholder="Recipient Name" className="w-full h-10 px-3 rounded-lg border border-slate-200 dark:border-slate-700 dark:bg-slate-800 text-sm" data-testid="ss-recipient" />
                  <input value={smartshipForm.address_street} onChange={e => setSmartshipForm(p => ({...p, address_street: e.target.value}))} placeholder="Street Address" className="w-full h-10 px-3 rounded-lg border border-slate-200 dark:border-slate-700 dark:bg-slate-800 text-sm" data-testid="ss-street" />
                  <div className="grid grid-cols-3 gap-2">
                    <input value={smartshipForm.address_city} onChange={e => setSmartshipForm(p => ({...p, address_city: e.target.value}))} placeholder="City" className="h-10 px-3 rounded-lg border border-slate-200 dark:border-slate-700 dark:bg-slate-800 text-sm" data-testid="ss-city" />
                    <input value={smartshipForm.address_state} onChange={e => setSmartshipForm(p => ({...p, address_state: e.target.value}))} placeholder="State" className="h-10 px-3 rounded-lg border border-slate-200 dark:border-slate-700 dark:bg-slate-800 text-sm" data-testid="ss-state" />
                    <input value={smartshipForm.address_zip} onChange={e => setSmartshipForm(p => ({...p, address_zip: e.target.value}))} placeholder="ZIP" className="h-10 px-3 rounded-lg border border-slate-200 dark:border-slate-700 dark:bg-slate-800 text-sm" data-testid="ss-zip" />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-xs text-slate-500 mb-1 block">Package Value ($)</label>
                      <input type="number" value={smartshipForm.package_value} onChange={e => setSmartshipForm(p => ({...p, package_value: parseFloat(e.target.value) || 0}))} className="w-full h-10 px-3 rounded-lg border border-slate-200 dark:border-slate-700 dark:bg-slate-800 text-sm" data-testid="ss-value" />
                    </div>
                    <div>
                      <label className="text-xs text-slate-500 mb-1 block">Delivery Method</label>
                      <select value={smartshipForm.delivery_method} onChange={e => setSmartshipForm(p => ({...p, delivery_method: e.target.value}))} className="w-full h-10 px-3 rounded-lg border border-slate-200 dark:border-slate-700 dark:bg-slate-800 text-sm" data-testid="ss-method">
                        <option value="standard">Standard</option>
                        <option value="signature">Signature</option>
                        <option value="leave_at_door">Leave at Door</option>
                        <option value="locker">Locker</option>
                        <option value="hold_for_pickup">Hold for Pickup</option>
                      </select>
                    </div>
                  </div>
                  <Button onClick={runSmartShip} disabled={loading || !smartshipForm.address_zip} className="w-full" data-testid="run-smartship">
                    {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <MapPin className="w-4 h-4 mr-2" />}
                    {loading ? 'Analyzing...' : 'Run SmartShip Analysis ($5.00)'}
                  </Button>
                </div>
              </>
            )}

            {activeTool === 'supertrak' && (
              <>
                <h3 className="font-semibold mb-4 flex items-center gap-2"><Radio className="w-4 h-4 text-purple-600" /> SuperTrak Monitoring</h3>
                <div className="space-y-3">
                  <input value={supertrakForm.shipment_id} onChange={e => setSupertrakForm(p => ({...p, shipment_id: e.target.value}))} placeholder="Shipment ID" className="w-full h-10 px-3 rounded-lg border border-slate-200 dark:border-slate-700 dark:bg-slate-800 text-sm" data-testid="st-shipment" />
                  <input type="number" value={supertrakForm.package_value} onChange={e => setSupertrakForm(p => ({...p, package_value: parseFloat(e.target.value) || 0}))} placeholder="Package Value ($)" className="w-full h-10 px-3 rounded-lg border border-slate-200 dark:border-slate-700 dark:bg-slate-800 text-sm" data-testid="st-value" />
                  <input value={supertrakForm.contact_phone} onChange={e => setSupertrakForm(p => ({...p, contact_phone: e.target.value}))} placeholder="Contact Phone" className="w-full h-10 px-3 rounded-lg border border-slate-200 dark:border-slate-700 dark:bg-slate-800 text-sm" data-testid="st-phone" />
                  <input value={supertrakForm.contact_email} onChange={e => setSupertrakForm(p => ({...p, contact_email: e.target.value}))} placeholder="Contact Email" className="w-full h-10 px-3 rounded-lg border border-slate-200 dark:border-slate-700 dark:bg-slate-800 text-sm" data-testid="st-email" />
                  <textarea value={supertrakForm.special_instructions} onChange={e => setSupertrakForm(p => ({...p, special_instructions: e.target.value}))} placeholder="Special Instructions (optional)" className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 dark:bg-slate-800 text-sm" rows={2} data-testid="st-instructions" />
                  <Button onClick={runSuperTrak} disabled={loading || !supertrakForm.shipment_id} className="w-full" data-testid="run-supertrak">
                    {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Radio className="w-4 h-4 mr-2" />}
                    {loading ? 'Activating...' : 'Activate SuperTrak ($5.00)'}
                  </Button>
                </div>
              </>
            )}

            {activeTool === 'preauth_checker' && (
              <>
                <h3 className="font-semibold mb-4 flex items-center gap-2"><CreditCard className="w-4 h-4 text-amber-600" /> PreAuth Chargeback Checker</h3>
                <div className="space-y-3">
                  <input value={preauthForm.transaction_id} onChange={e => setPreauthForm(p => ({...p, transaction_id: e.target.value}))} placeholder="Transaction ID" className="w-full h-10 px-3 rounded-lg border border-slate-200 dark:border-slate-700 dark:bg-slate-800 text-sm" data-testid="pa-txn" />
                  <div className="grid grid-cols-2 gap-2">
                    <input type="number" value={preauthForm.amount} onChange={e => setPreauthForm(p => ({...p, amount: parseFloat(e.target.value) || 0}))} placeholder="Amount ($)" className="h-10 px-3 rounded-lg border border-slate-200 dark:border-slate-700 dark:bg-slate-800 text-sm" data-testid="pa-amount" />
                    <input value={preauthForm.card_last4} onChange={e => setPreauthForm(p => ({...p, card_last4: e.target.value}))} placeholder="Card Last 4" maxLength={4} className="h-10 px-3 rounded-lg border border-slate-200 dark:border-slate-700 dark:bg-slate-800 text-sm" data-testid="pa-card" />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <input value={preauthForm.billing_zip} onChange={e => setPreauthForm(p => ({...p, billing_zip: e.target.value}))} placeholder="Billing ZIP" className="h-10 px-3 rounded-lg border border-slate-200 dark:border-slate-700 dark:bg-slate-800 text-sm" data-testid="pa-bzip" />
                    <input value={preauthForm.shipping_zip} onChange={e => setPreauthForm(p => ({...p, shipping_zip: e.target.value}))} placeholder="Shipping ZIP" className="h-10 px-3 rounded-lg border border-slate-200 dark:border-slate-700 dark:bg-slate-800 text-sm" data-testid="pa-szip" />
                  </div>
                  <input value={preauthForm.email} onChange={e => setPreauthForm(p => ({...p, email: e.target.value}))} placeholder="Customer Email" className="w-full h-10 px-3 rounded-lg border border-slate-200 dark:border-slate-700 dark:bg-slate-800 text-sm" data-testid="pa-email" />
                  <input value={preauthForm.ip_address} onChange={e => setPreauthForm(p => ({...p, ip_address: e.target.value}))} placeholder="IP Address (optional)" className="w-full h-10 px-3 rounded-lg border border-slate-200 dark:border-slate-700 dark:bg-slate-800 text-sm" data-testid="pa-ip" />
                  <Button onClick={runPreAuth} disabled={loading || !preauthForm.transaction_id} className="w-full" data-testid="run-preauth">
                    {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <CreditCard className="w-4 h-4 mr-2" />}
                    {loading ? 'Analyzing...' : 'Run PreAuth Check ($3.99)'}
                  </Button>
                </div>
              </>
            )}

            {activeTool === 'supercheck_plus' && (
              <>
                <h3 className="font-semibold mb-4 flex items-center gap-2"><Shield className="w-4 h-4 text-emerald-600" /> SuperCheck Plus</h3>
                <div className="space-y-3">
                  <input value={supercheckForm.shipment_id} onChange={e => setSupercheckForm(p => ({...p, shipment_id: e.target.value}))} placeholder="Shipment ID" className="w-full h-10 px-3 rounded-lg border border-slate-200 dark:border-slate-700 dark:bg-slate-800 text-sm" data-testid="sc-shipment" />
                  <input type="number" value={supercheckForm.package_value} onChange={e => setSupercheckForm(p => ({...p, package_value: parseFloat(e.target.value) || 0}))} placeholder="Package Value ($)" className="w-full h-10 px-3 rounded-lg border border-slate-200 dark:border-slate-700 dark:bg-slate-800 text-sm" data-testid="sc-value" />
                  <textarea value={supercheckForm.item_description} onChange={e => setSupercheckForm(p => ({...p, item_description: e.target.value}))} placeholder="Item Description" className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 dark:bg-slate-800 text-sm" rows={2} data-testid="sc-desc" />
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={supercheckForm.sender_verified} onChange={e => setSupercheckForm(p => ({...p, sender_verified: e.target.checked}))} className="rounded" /> Sender Verified</label>
                    <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={supercheckForm.recipient_verified} onChange={e => setSupercheckForm(p => ({...p, recipient_verified: e.target.checked}))} className="rounded" /> Recipient Verified</label>
                  </div>
                  <Button onClick={runSuperCheck} disabled={loading || !supercheckForm.shipment_id} className="w-full" data-testid="run-supercheck">
                    {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Shield className="w-4 h-4 mr-2" />}
                    {loading ? 'Activating...' : 'Activate SuperCheck Plus ($35.00)'}
                  </Button>
                </div>
              </>
            )}
          </div>

          {/* Results Panel */}
          <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-6">
            {loading && (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-6 h-6 text-blue-500 animate-spin" />
              </div>
            )}

            {result?.error && (
              <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg text-red-700 dark:text-red-400 text-sm" data-testid="tool-error">
                <AlertTriangle className="w-4 h-4 inline mr-1" /> {result.error}
              </div>
            )}

            {result?.tool === 'smartship' && result.data && (
              <div className="space-y-4" data-testid="smartship-result">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold">SmartShip Report</h3>
                  <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${riskColor(result.data.overall.risk_level)}`}>
                    Risk: {result.data.overall.risk_level.toUpperCase()} ({result.data.overall.risk_score})
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800">
                    <p className="text-xs text-slate-400 mb-1">Crime Score</p>
                    <p className="font-semibold tabular-nums">{result.data.property_analysis.crime_score}/100</p>
                    <p className="text-xs text-slate-500">{result.data.property_analysis.crime_level}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800">
                    <p className="text-xs text-slate-400 mb-1">Theft Rate</p>
                    <p className="font-semibold tabular-nums">{result.data.property_analysis.theft_rate}</p>
                    <p className="text-xs text-slate-500">Porch piracy: {result.data.property_analysis.porch_piracy_risk}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800">
                    <p className="text-xs text-slate-400 mb-1">Person Verified</p>
                    <p className="font-semibold">{result.data.person_verification.verified ? 'Yes' : 'No'}</p>
                    <p className="text-xs text-slate-500">Score: {result.data.person_verification.association_score}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800">
                    <p className="text-xs text-slate-400 mb-1">Delivery Risk</p>
                    <p className="font-semibold">{result.data.delivery_analysis.risk_level}</p>
                    <p className="text-xs text-slate-500">Rec: {result.data.delivery_analysis.recommended_method}</p>
                  </div>
                </div>
                {result.data.recommendations.length > 0 && (
                  <div className="pt-3 border-t border-slate-100 dark:border-slate-800">
                    <p className="text-xs font-medium text-slate-400 mb-2">RECOMMENDATIONS</p>
                    <ul className="space-y-1.5">
                      {result.data.recommendations.map((rec, i) => (
                        <li key={i} className="text-sm flex items-start gap-2">
                          <ChevronRight className="w-3 h-3 mt-1 text-blue-500 flex-shrink-0" />
                          {rec}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {result?.tool === 'supertrak' && result.data && (
              <div className="space-y-4" data-testid="supertrak-result">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold">SuperTrak Active</h3>
                  <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">MONITORING</span>
                </div>
                <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-purple-100 dark:bg-purple-800 flex items-center justify-center">
                      <User className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-sm">{result.data.assigned_agent.name}</p>
                      <p className="text-xs text-slate-500">{result.data.assigned_agent.message}</p>
                    </div>
                  </div>
                </div>
                <div className="text-sm space-y-2">
                  {result.data.monitoring.features.map((f, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <CheckCircle className="w-3.5 h-3.5 text-emerald-500" /> {f}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {result?.tool === 'preauth_checker' && result.data && (
              <div className="space-y-4" data-testid="preauth-result">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold">PreAuth Check Report</h3>
                  <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${
                    result.data.recommendation.action === 'approve' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' :
                    result.data.recommendation.action === 'review' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300' :
                    'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
                  }`}>
                    {result.data.recommendation.action.toUpperCase()}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800">
                    <p className="text-xs text-slate-400 mb-1">Chargeback Risk</p>
                    <p className="font-semibold tabular-nums">{result.data.analysis.chargeback_probability}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800">
                    <p className="text-xs text-slate-400 mb-1">Anomalies</p>
                    <p className="font-semibold tabular-nums">{result.data.analysis.anomalies_found}</p>
                  </div>
                </div>
                {result.data.analysis.anomalies.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-slate-400 mb-2">ANOMALIES DETECTED</p>
                    {result.data.analysis.anomalies.map((a, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm py-1">
                        <AlertTriangle className={`w-3.5 h-3.5 ${a.severity === 'high' ? 'text-red-500' : 'text-amber-500'}`} />
                        {a.description}
                      </div>
                    ))}
                  </div>
                )}
                <div className="pt-3 border-t border-slate-100 dark:border-slate-800 text-sm">
                  <p className="font-medium">{result.data.recommendation.reason}</p>
                  <p className="text-xs text-slate-500 mt-1">Confidence: {result.data.recommendation.confidence}</p>
                </div>
              </div>
            )}

            {result?.tool === 'supercheck_plus' && result.data && (
              <div className="space-y-4" data-testid="supercheck-result">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold">SuperCheck Plus Activated</h3>
                  <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">ACTIVE</span>
                </div>
                <div className="p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg text-sm">
                  <p className="font-medium">Certificate: {result.data.certificate_number}</p>
                  <p className="text-slate-500 mt-1">Coverage: {result.data.coverage.insurance_amount}</p>
                  <p className="text-slate-500">Retrieval: {result.data.coverage.retrieval_guarantee}</p>
                </div>
                <div className="text-sm space-y-2">
                  {result.data.protection_includes.map((f, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <CheckCircle className="w-3.5 h-3.5 text-emerald-500" /> {f}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {!result && !loading && (
              <div className="text-center py-12">
                <Eye className="w-8 h-8 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-400 text-sm">Select a tool and run an analysis to see results</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PremiumToolsPage;
